import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import { getQuizOptionsData } from '../../constants';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';

// Step 1 mirrors create-friend's Step0 layout/behavior — just with ethnicity
// options (Asian / Caucasian) instead of the Realistic / Anime style chooser.
const Step1 = ({ onAnswer, selections }) => {
  const { animationMode, getVideoProps } = useVideoAnimation();
  const { videos: gbVideos } = useGrowthBookValues();

  const ethnicities = getQuizOptionsData('ethnicity');

  // GB override > hardcoded constant.
  const resolveVideoSrc = (option) =>
    gbVideos?.step1?.[option.value] || option.video;

  const [selectedOption, setSelectedOption] = useState(
    selections?.step1?.ethnicity || ethnicities[0]?.value || '',
  );
  const videoRefs = useRef([]);
  const [videosReady, setVideosReady] = useState(false);

  const { t } = useTranslation();

  const handleOptionClick = useCallback((value) => {
    setSelectedOption(value);
  }, []);

  // Wire up videos (mute/loop/playsInline + try-play on every readiness event).
  useEffect(() => {
    const cleanups = [];

    videoRefs.current.forEach((videoRef) => {
      if (!videoRef) return;

      videoRef.muted = true;
      videoRef.loop = true;
      videoRef.playsInline = true;
      videoRef.setAttribute('playsinline', '');
      videoRef.setAttribute('webkit-playsinline', '');
      videoRef.preload = 'auto';
      videoRef.controls = false;
      videoRef.removeAttribute('controls');
      videoRef.removeAttribute('controlsList');

      const tryPlay = () => {
        if (videoRef.paused) videoRef.play().catch(() => {});
      };

      videoRef.addEventListener('loadedmetadata', tryPlay);
      videoRef.addEventListener('canplay', tryPlay);
      videoRef.addEventListener('loadeddata', tryPlay);

      if (videoRef.readyState >= 3) tryPlay();

      cleanups.push(() => {
        videoRef.removeEventListener('loadedmetadata', tryPlay);
        videoRef.removeEventListener('canplay', tryPlay);
        videoRef.removeEventListener('loadeddata', tryPlay);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [animationMode, ethnicities]);

  const playVideo = useCallback((index) => {
    const video = videoRefs.current[index];
    if (video && video.paused) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }, []);

  // AUTOMATIC: kick off the selected option's video once the first video is ready.
  useEffect(() => {
    if (!videosReady || !selectedOption) return;
    if (animationMode !== 'AUTOMATIC') return;

    const idx = ethnicities.findIndex((opt) => opt.value === selectedOption);
    if (idx === -1) return;

    const timer = setTimeout(() => playVideo(idx), 100);
    return () => clearTimeout(timer);
  }, [videosReady, selectedOption, ethnicities, playVideo, animationMode]);

  const handleVideoCanPlay = useCallback((index) => {
    if (index === 0) setVideosReady(true);
    const video = videoRefs.current[index];
    if (video && video.paused) video.play().catch(() => {});
  }, []);

  // Step 1 (mirrors create-friend Step 0): both videos must always play
  // regardless of background/visibility state. Three-layer watchdog handles
  // visibility changes, per-video pauses, and frozen Safari decoders.
  useEffect(() => {
    let resumeTimer = null;
    const lastTimes = {};

    const forcePlay = (video, retriesLeft = 3) => {
      video.currentTime = 0;
      video.play().catch(() => {
        if (retriesLeft > 0) {
          setTimeout(() => forcePlay(video, retriesLeft - 1), 500);
        }
      });
    };

    const playAll = () => {
      videoRefs.current.forEach((video) => {
        if (video) forcePlay(video);
      });
    };

    const schedulePlayAll = () => {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(playAll, 200);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') schedulePlayAll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', schedulePlayAll);

    const pauseCleanups = [];
    videoRefs.current.forEach((video) => {
      if (!video) return;
      const onPause = () => {
        if (document.visibilityState === 'hidden') schedulePlayAll();
      };
      video.addEventListener('pause', onPause);
      pauseCleanups.push(() => video.removeEventListener('pause', onPause));
    });

    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      videoRefs.current.forEach((video, i) => {
        if (!video) return;
        if (video.paused) {
          forcePlay(video);
        } else if (video.currentTime === lastTimes[i]) {
          forcePlay(video);
        }
        lastTimes[i] = video.currentTime;
      });
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', schedulePlayAll);
      pauseCleanups.forEach((fn) => fn());
      clearInterval(watchdog);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, []);

  const handleContinue = () => {
    if (!selectedOption) {
      alert(t('select_option'));
      return;
    }
    onAnswer('step1', { ethnicity: selectedOption });
  };

  const canContinue = !!selectedOption;

  return (
    <div className="quizContentWrapper quizContentWrapperStep0">
      <QuizContent className="mainStep">
        <QuizTitle>
          {t('create')}
          <span className="specialText-valentine"> {t('your_own')} </span>
          <span className="specialText"> {t('ai_trans_fucktoy')} </span>
        </QuizTitle>
        <div className="step0Wrapper">
          <QuizOptions className="step0-options">
            {ethnicities.map((option, index) => {
              const isSelected = selectedOption === option.value;

              return (
                <QuizOption
                  key={option.value}
                  value={option.value}
                  name="ethnicity"
                  onClick={() => handleOptionClick(option.value)}
                  className={`step0-option ${
                    isSelected
                      ? 'selected'
                      : selectedOption !== null
                      ? 'not-selected'
                      : ''
                  }`}
                >
                  <div className="option-image">
                    <video
                      ref={(el) => (videoRefs.current[index] = el)}
                      className="step0Video"
                      {...getVideoProps()}
                      src={resolveVideoSrc(option)}
                      preload="auto"
                      controls={false}
                      controlsList="nodownload nofullscreen noremoteplayback"
                      playsInline
                      webkit-playsinline=""
                      onCanPlay={() => handleVideoCanPlay(index)}
                    />
                    <div className="step0-image-title">{t(option.text)}</div>
                  </div>
                </QuizOption>
              );
            })}
          </QuizOptions>

          <button
            disabled={!canContinue}
            className="continue-button step0"
            onClick={handleContinue}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step1;
